import { graphql, useStaticQuery } from 'gatsby';
import { useMergePrismicPreviewData } from 'gatsby-plugin-prismic-previews';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@emotion/react';
import { gsap } from 'gsap';

import { FOOTER_LINK_TYPE } from './constants';
import { FooterLink, LayoutFooterProps, OrderedFooterLink } from './types';
import { composeCtaAnimationData, START_ANIMATION_CLASS } from './animations';
import { ICTA } from './CTA';

import { ILanguages } from '../types';

import { richTextWithPlaceholder } from 'utils/prismic';

export const useFooter = ({
  currentLanguage,
  prependFooterLinks,
  footerLinkRows,
}: Omit<LayoutFooterProps, 'autoFooterPadding'>) => {
  const allLanguagesData = useFooterData();

  const { links_title, links, contact_links } = allLanguagesData.allPrismicFooter.edges.reduce(
    (acm, { node }) => {
      if (node.lang === currentLanguage) {
        return node.data;
      } else return acm;
    },
    {} as Queries.FooterDataFragment
  );

  return {
    ...useFooterAnimationTrigger(),
    ctas: useFooterCTAs({ currentLanguage, data: allLanguagesData }),
    linksTitle: richTextWithPlaceholder(links_title, ''),
    contactLinks: useFooterContactLinks(contact_links as Queries.FooterContactLinkFragment[]),
    ...useFooterLinks({
      linksData: links as Queries.FooterLinkFragment[],
      prependFooterLinks,
      footerLinkRows,
    }),
  };
};

export const useCTA = ({ ctas, animationTriggerElement }: ICTA) => {
  const ctasRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const context = gsap.context(() => {
      gsap.to(ctasRef.current, {
        scrollTrigger: {
          trigger: animationTriggerElement,
          start: 'bottom bottom',
        },
        onStart: () => {
          ctasRef.current?.classList.add(START_ANIMATION_CLASS);
        },
      });
    });

    return () => context.revert();
  }, [animationTriggerElement]);

  const { durations } = useTheme();

  const { delays, ...animationData } = useMemo(
    () =>
      composeCtaAnimationData({
        ctasCount: ctas.length,
        finalItemIterations: 8,
        baseAnimationMs: Number(durations.long.slice(0, -2)) * 2,
      }),
    [ctas, durations.long]
  );

  return { ctasRef, delays, animationData };
};

const useFooterAnimationTrigger = () => {
  const animationTriggerRef = useRef<HTMLDivElement>(null);
  const [animationTriggerElement, setAnimationTriggerElement] = useState<HTMLDivElement | null>(
    null
  );

  const [resizeId, setResizeId] = useState(0);

  useEffect(() => {
    const handleResize = () => setResizeId(id => id + 1);
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    if (animationTriggerRef.current) setAnimationTriggerElement(animationTriggerRef.current);
  }, [animationTriggerRef.current, resizeId]);

  return { animationTriggerRef, animationTriggerElement };
};

const useFooterCTAs = ({
  currentLanguage,
  data,
}: {
  currentLanguage: ILanguages['currentLanguage'];
  data: Queries.FooterQuery;
}) => {
  const languageCTAs = data.allPrismicFooter.edges.reduce((acm, { node }) => {
    const { lang, data } = node;

    if (data.call_to_action) {
      return [...acm, { language: lang, cta: data.call_to_action ?? '' }];
    }

    return acm;
  }, []);

  const currentLanguageCTAIndex = languageCTAs.findIndex(
    ({ language }) => language === currentLanguage
  );

  const normalizedCTAIndex = currentLanguageCTAIndex < 0 ? 0 : currentLanguageCTAIndex;

  const currentCTA = languageCTAs[normalizedCTAIndex];

  const sortedCTAs = [
    currentCTA,
    ...languageCTAs.slice(0, normalizedCTAIndex),
    ...languageCTAs.slice(normalizedCTAIndex + 1),
  ];

  const rotatedCTAS = [...sortedCTAs.slice(-1), ...sortedCTAs.slice(0, -1)];

  return [...rotatedCTAS, currentCTA].map(({ cta }) => cta);
};

const useFooterLinks = ({
  linksData,
  prependFooterLinks = [],
  footerLinkRows = 3,
}: { linksData: Queries.FooterLinkFragment[] | null } & Pick<
  LayoutFooterProps,
  'prependFooterLinks' | 'footerLinkRows'
>) => {
  const footerLinks: FooterLink[] =
    linksData?.reduce((acm, { link_label, link_content }) => {
      if (!link_content?.url || !link_label) return acm;

      const { url, type, target } = link_content;
      return [
        ...acm,
        {
          label: link_label,
          url,
          type: type ? FOOTER_LINK_TYPE.INTERNAL : FOOTER_LINK_TYPE.EXTERNAL,
          target: target ?? '',
        },
      ];
    }, []) ?? [];

  const links = [...prependFooterLinks, ...footerLinks];

  const numberOfColumns = Math.ceil(links.length / footerLinkRows);

  const { matrix: gridLikeMatrix } = [...new Array(links.length)].reduce(
    ({ rowsLeft, matrix }, _, index) => {
      const newRowsLeft = (index + 1) % numberOfColumns === 0 ? rowsLeft - 1 : rowsLeft;

      const currentRowIndex = footerLinkRows - rowsLeft;

      const currentRow = [...(matrix?.[currentRowIndex] ?? []), index];

      const isLastIndex = index + 1 === links.length;

      const nullFilledColumnRow = isLastIndex
        ? [...currentRow, ...new Array(numberOfColumns - currentRow.length).map(() => null)]
        : currentRow;

      const reversedByColumnRow =
        isLastIndex || rowsLeft !== newRowsLeft
          ? nullFilledColumnRow.reverse()
          : nullFilledColumnRow;

      const previousRows = matrix.slice(0, currentRowIndex);

      return {
        rowsLeft: newRowsLeft,
        matrix: [...previousRows, reversedByColumnRow],
      };
    },
    {
      rowsLeft: footerLinkRows,
      matrix: [],
    } as { rowsLeft: number; matrix: number[][] }
  );

  const rowsPerColumn = [...new Array(numberOfColumns)].reduce((acm, row, columnIndex) => {
    const rowsInColumn = gridLikeMatrix.reduce((acm, row) => {
      return row[columnIndex] === void 0 ? acm : acm + 1;
    }, 0);

    return [...acm, rowsInColumn];
  }, [] as number[]);

  const { orderedLinks } = links.reduce(
    ({ rowsPerColumnLeft, orderedLinks }, link) => {
      const columnIndex = numberOfColumns - rowsPerColumnLeft.length;

      const [rowsLeftInCurrentColumn] = rowsPerColumnLeft;

      const rowsTotalInCurrentColumn = rowsPerColumn[columnIndex];

      const rowIndex = rowsTotalInCurrentColumn - rowsLeftInCurrentColumn;

      const otherRowsPerColumnLefts = [...rowsPerColumnLeft].slice(1);

      const newRowsPerColumnLeft =
        rowsLeftInCurrentColumn - 1 === 0
          ? otherRowsPerColumnLefts
          : [rowsLeftInCurrentColumn - 1, ...otherRowsPerColumnLefts];

      return {
        rowsPerColumnLeft: newRowsPerColumnLeft,
        orderedLinks: [...orderedLinks, { ...link, order: gridLikeMatrix[rowIndex][columnIndex] }],
      };
    },
    { rowsPerColumnLeft: rowsPerColumn, orderedLinks: [] } as {
      rowsPerColumnLeft: number[];
      orderedLinks: OrderedFooterLink[];
    }
  );

  return {
    links: orderedLinks,
    linksColumnCount: numberOfColumns,
    linksRowCount: footerLinkRows,
  };
};

const useFooterContactLinks = (contactLinks: Queries.FooterContactLinkFragment[] | null) => {
  return (
    contactLinks?.reduce((acm, { contact_link_label, contact_link_address }) => {
      if (!contact_link_address) return acm;

      return [
        ...acm,
        {
          label: contact_link_label,
          address: contact_link_address,
        },
      ];
    }, []) ?? []
  );
};

const useFooterData = () => {
  const staticFooterData = useStaticQuery<Queries.FooterQuery>(footerQuery);

  const { data } = useMergePrismicPreviewData(staticFooterData);

  return data;
};

const footerQuery = graphql`
  query Footer {
    allPrismicFooter {
      edges {
        node {
          _previewable
          lang
          data {
            ...FooterData
          }
        }
      }
    }
  }

  fragment FooterData on PrismicFooterDataType {
    call_to_action
    links_title {
      richText
    }
    links {
      ...FooterLink
    }
    contact_links {
      ...FooterContactLink
    }
  }

  fragment FooterLink on PrismicFooterDataLinks {
    link_label
    link_content {
      url
      type
      target
    }
  }

  fragment FooterContactLink on PrismicFooterDataContactLinks {
    contact_link_address
    contact_link_label
  }
`;
